import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  input,
} from "@angular/core";
import { firstValueFrom } from "rxjs";
import { DialogsService } from "app/shared/services/dialogs/dialogs.service";
import {
  ConfirmationDetail,
  ConfirmationDialogComponent,
} from "../confirmation-dialog/confirmation-dialog.component";
import { ChipsComponent } from "../../modules/chips/chips.component";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslatePipe } from "../../pipes/translate.pipe";
import {
  SyncLinkApiService,
} from "app/features/connector/service/sync-link-api.service";
import { SyncObjectType } from "app/features/connector/models/sync-link.model";

/**
 * C15 warn-before-delete hint. When provided, the delete button pre-flights
 * a GET /sync-link/has-link request on click. If a SyncLink exists the
 * confirmation dialog warns the user that the element is tied to an external
 * connector (PrestaShop) and that deleting locally will not remove it there.
 */
export interface SyncLinkCheckInput {
  type: SyncObjectType;
  id: string;
  /** Display name of the entity, used in the warning message. */
  entityLabel?: string;
}

@Component({
  selector: "vl-delete-button",
  standalone: true,
  imports: [
    ChipsComponent,
    MatTooltip,
    MatIcon,
    MatProgressSpinner,
    TranslatePipe,
  ],
  templateUrl: "./delete-button.component.html",
  styleUrls: ["./delete-button.component.scss"],
})
export class DeleteButtonComponent {
  @Input() disabled: boolean;
  @Output() delete = new EventEmitter();
  size = input<"small" | "normal">("normal");
  loading = input(false);

  /**
   * G5 — when set, the button checks the SyncLink registry before showing the
   * confirmation dialog. If the entity is linked to a connector, the dialog
   * message is extended with a warning.
   */
  syncLinkCheck = input<SyncLinkCheckInput | null>(null);

  /**
   * Titre et message de la confirmation, quand « supprimer » ne décrit pas ce qui va se passer.
   *
   * ⚠️ **Le texte par défaut promet une suppression, et ce n'est pas toujours vrai.** Sur les
   * énumérations, le serveur tente un `DELETE` et, si une clé étrangère l'en empêche, **désactive**
   * l'élément à la place (`@NoTransaction` sur l'endpoint, précisément pour attraper l'erreur au
   * commit). L'utilisateur croit donc avoir supprimé une famille technique qui reste employée par des
   * fiches article — constaté en recette. Un bouton qui annonce autre chose que ce qu'il fait est
   * pire qu'un bouton sans explication.
   *
   * Laisser vide conserve le texte historique : aucun appelant existant ne change de comportement.
   */
  confirmTitle = input<string | null>(null);
  confirmMessage = input<string | null>(null);

  /**
   * La **ventilation dépliable** affichée sous le message : « de quoi » est fait le compte annoncé.
   *
   * ⚠️ **Le message ne peut en nommer que deux** sans devenir un paragraphe ; une valeur employée par
   * cinq tables laissait donc l'utilisateur sans moyen de savoir où sont les trois autres. Le dialogue
   * la rend repliée par défaut : la décision demande la conséquence, pas l'inventaire.
   *
   * ⚠️ **Les libellés arrivent DÉJÀ traduits** : le bouton est générique, il ne sait pas que
   * `article-infos` se dit « versions de fiche article ».
   */
  confirmDetails = input<ConfirmationDetail[]>([]);

  /**
   * Le libellé du bouton de confirmation, quand « Confirmer » ne dit pas ce qu'on confirme.
   *
   * ⚠️ **Sur les énumérations, l'issue est connue** : « Archiver » ou « Supprimer ». Garder « Confirmer »
   * juste avant un geste irréversible laisse la question ouverte au dernier moment. Vide = comportement
   * historique (`button.confirm`), donc aucun appelant existant ne change.
   */
  confirmActionLabel = input<string>("");

  constructor(
    private dialogsService: DialogsService,
    private syncLinkApi: SyncLinkApiService,
  ) {}

  async onDelete() {
    // L'input `disabled` était déclaré mais JAMAIS appliqué (ni [disabled] sur le
    // bouton natif, ni garde ici) : les 3 call sites qui le bindent
    // (custom-enum canDelete, cost, article-planif) n'étaient pas protégés.
    if (this.disabled) return;
    let warning: string | undefined;
    const check = this.syncLinkCheck();
    if (check?.type && check?.id) {
      try {
        const res = await firstValueFrom(
          this.syncLinkApi.hasLink(check.type, check.id),
        );
        if (res.hasLink) {
          const label = check.entityLabel ?? "cet élément";
          warning =
            `${label} est lié à un connecteur e-commerce. ` +
            `La suppression dans Justup ne le retire pas de la boutique en ligne, ` +
            `et les futures modifications ne seront plus importées automatiquement.`;
        }
      } catch {
        // Best-effort: if the API call fails, we still show the standard dialog.
      }
    }

    /**
     * ⚠️ L'avertissement connecteur PRIME sur le message d'appel : il porte une conséquence externe
     * (la boutique en ligne garde l'élément), que le texte métier ne dit pas.
     */
    const message = warning
      ? `${warning}\n\nConfirmez-vous la suppression ?`
      : (this.confirmMessage() ??
        "Vous êtes sur le point de supprimer cet élément. Confirmez-vous ?");

    const dialogRef = this.dialogsService.open(
      ConfirmationDialogComponent,
      "fit-content",
      "fit-content",
      {
        title: warning
          ? "Suppression (lien connecteur)"
          : (this.confirmTitle() ?? "Suppression"),
        message,
        /**
         * ⚠️ **Pas de ventilation quand l'avertissement connecteur prend le dessus** : ce message-là
         * parle d'une conséquence EXTERNE (la boutique en ligne garde l'élément), et un détail
         * d'emplois internes affiché dessous se lirait comme la ventilation de cet avertissement.
         */
        details: warning ? [] : this.confirmDetails(),
        // Vide ⇒ le dialogue retombe sur `button.confirm` : aucun appelant existant n'est touché.
        confirmText: this.confirmActionLabel() || undefined,
      },
    );

    const sub = dialogRef.afterClosed().subscribe((res: boolean) => {
      if (res) {
        this.delete.emit();
      }
      sub.unsubscribe();
    });
  }
}
